import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { PageShell } from '../../../components/leaves/PageShell';
import { Users } from 'lucide-react';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { cn } from '../../../utils/cn';
import { appDateTime, appTime } from '../../../utils/appDateFormat';
import { Roles } from '../../../components/roles';
import TeamFilters from './components/TeamFilters';
import AttendanceTable from './components/AttendanceTable';
import KPIDashboard from './components/KPIDashboard';
import AddMissingLogModal from './components/AddMissingLogModal';
import AddRemarkModal from './components/AddRemarkModal';
import CorrectionRequestReviewModal from './components/CorrectionRequestReviewModal';
import AddAttendanceEntryModal from './components/AddAttendanceEntryModal';
import DayRecordDetailsModal from './components/DayRecordDetailsModal';
import Pagination from '../../../components/Pagination';
import { getUsersStatuses, exportUsersStatuses } from '../../../api/Attendance/reports';
import { FetchUsers } from '../../../api/General/account';
import { getBranches, getDepartments } from '../../../api/General/Company';
import { getUsersCountries } from '../../../api/General/Country';
import { addShift, clock, getOpenShifts, updateAttendanceRemark } from '../../../api/Attendance/attendance';
import { ClockingType } from '../../../models/attendance';
import { ClientType } from '../../../models/general';
import {
  getAssignedCorrectionRequests,
  decideCorrectionRequest,
} from '../../../api/Attendance/correctionRequests';

const STATUS_LABEL_TO_ENUM = {
  'Present': ['Available'],
  'Partial': ['WorkingHoursNotFulfilled'],
  'Absent': ['Absent'],
  'Leave': ['Leave', 'SickLeave', 'HalfDayLeave'],
  'Holiday': ['Holiday'],
  'Future': ['Future'],
  'Non-Working Day': ['NonWorkingDay'],
};

const TeamAttendancePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userRole = useSelector((state) => state?.user?.role);
  const userId = useSelector((state) => state?.user?.userId);
  const companyId = useSelector((state) => state?.user?.company?.id);

  const canEditAttendance = Roles.HR.includes(userRole);

  const canViewTeamAttendance =
    Roles.MANAGER.includes(userRole) || Roles.HR.includes(userRole) || Roles.CHIEF.includes(userRole);

  if (!canViewTeamAttendance) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="warning" size={24} />
          <h2 className="text-xl font-semibold">{t('Access denied')}</h2>
        </div>
        <p className="text-muted-foreground">{t("You don't have permission to view Team Attendance.")}</p>
        <button
          type="button"
          onClick={() => navigate('/attendance')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded"
        >
          {t('Back to Attendance')}
        </button>
      </div>
    );
  }

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Filter states
  const [selectedDateRange, setSelectedDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    end: new Date()
  });
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const [departmentOptions, setDepartmentOptions] = useState([{ id: 'all', name: t('All Departments') }]);
  const [locationOptions, setLocationOptions] = useState([{ id: 'all', name: t('All Locations') }]);
  
  // Table states
  const [attendanceData, setAttendanceData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  const [companyUsers, setCompanyUsers] = useState([]);
  const [reloadCounter, setReloadCounter] = useState(0);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showNonWorkingDays, setShowNonWorkingDays] = useState(false);
  const [serverKpis, setServerKpis] = useState({ attendanceRate: 0, partialCount: 0, totalOvertime: 0 });

  // Modal states
  const [showAddLogModal, setShowAddLogModal] = useState(false);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [showApproveRejectModal, setShowApproveRejectModal] = useState(false);
  const [showAddAttendanceModal, setShowAddAttendanceModal] = useState(false);
  const [showDayDetailsModal, setShowDayDetailsModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  // In-flight lock for the Add Missing Log dialog. Closing an open entry is a single non-idempotent
  // punch, so a double-click must not be able to fire it twice.
  const [savingMissingLog, setSavingMissingLog] = useState(false);

  const { toast, showToast, dismissToast } = useToast();

  const matchesLocationForUser = (u, locationValue) => {
    if (!locationValue || locationValue === 'all') return true;
    const { branchIds, countryIds } = parseLocationSelection(locationValue);
    if (branchIds?.length) return Number(u?.branchId ?? u?.branch?.id ?? null) === Number(branchIds[0]);
    if (countryIds?.length) return Number(u?.countryId ?? u?.country?.id ?? null) === Number(countryIds[0]);
    return true;
  };

  const matchesDepartmentForUser = (u, departmentValue) => {
    if (!departmentValue || departmentValue === 'all') return true;
    return String(u?.departmentId ?? u?.department?.id ?? '') === String(departmentValue);
  };

  const formatCorrectionType = (type) => {
    if (!type) return t('Correction');
    switch (String(type).toLowerCase()) {
      case 'missing-clockout':
        return t('Missing Clock Out');
      case 'incorrect-time':
        return t('Incorrect Time');
      case 'missing-entry':
        return t('Missing Entry');
      case 'break-time':
        return t('Break Time');
      default:
        return String(type);
    }
  };

  const ymdLocal = (d) => {
    if (!d) return '';
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const formatTimeHHmm = (d) => {
    if (!d) return '-';
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return '-';
    return appTime(dt);
  };

  const mapUserStatusToLabel = (status) => {
    if (status === null || status === undefined) return 'Unknown';
    // Backend sends enum values; client enum mapping may serialize as number.
    const s = String(status).toLowerCase();
    if (s === 'available' || s === '0') return 'Present';
    if (s === 'workinghoursnotfulfilled' || s === '1') return 'Partial';
    if (s === 'absent' || s === '2') return 'Absent';
    if (s === 'leave' || s === '3') return 'Leave';
    if (s === 'sickleave' || s === '4') return 'Leave';
    if (s === 'holiday' || s === '5') return 'Holiday';
    if (s === 'future' || s === '6') return 'Future';
    if (s === 'nonworkingday' || s === 'non-working day' || s === 'weekend' || s === '7') return 'Non-Working Day';
    if (s === 'halfdayleave' || s === '8') return 'Leave';
    return 'Unknown';
  };

  const ShimmerBlock = ({ className = '' }) => (
    <div className={cn('bg-muted animate-pulse rounded', className)} />
  );

  const LoadingShimmer = () => (
    <div className="space-y-6" aria-busy="true" aria-live="polite">
      {/* KPI skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {[0, 1, 2].map((i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <ShimmerBlock className="w-10 h-10 rounded-lg" />
                <div className="space-y-2">
                  <ShimmerBlock className="h-4 w-40 rounded" />
                  <ShimmerBlock className="h-3 w-56 rounded" />
                </div>
              </div>
              <ShimmerBlock className="w-8 h-8 rounded-lg" />
            </div>
            <div className="space-y-3">
              <div className="flex items-end gap-2">
                <ShimmerBlock className="h-9 w-24 rounded" />
                <ShimmerBlock className="h-4 w-16 rounded" />
              </div>
              <ShimmerBlock className="h-2 w-full rounded-full" />
              <div className="flex justify-between">
                <ShimmerBlock className="h-3 w-24 rounded" />
                <ShimmerBlock className="h-3 w-28 rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters skeleton */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="space-y-2">
            <ShimmerBlock className="h-4 w-40 rounded" />
            <ShimmerBlock className="h-9 w-80 rounded-lg" />
          </div>
          <div className="flex flex-wrap gap-2">
            {[0, 1, 2, 3].map((i) => (
              <ShimmerBlock key={i} className="h-8 w-20 rounded-md" />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="space-y-2">
              <ShimmerBlock className="h-4 w-28 rounded" />
              <ShimmerBlock className="h-10 w-full rounded-lg" />
            </div>
          ))}
        </div>
      </div>

      {/* Table skeleton */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div className="space-y-2">
            <ShimmerBlock className="h-6 w-56 rounded" />
            <ShimmerBlock className="h-4 w-96 rounded" />
          </div>
          <div className="flex items-center gap-3">
            <ShimmerBlock className="h-4 w-28 rounded" />
            <ShimmerBlock className="h-10 w-44 rounded-lg" />
          </div>
        </div>
        <div className="p-6 space-y-3">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <ShimmerBlock key={i} className="h-10 w-full rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  );

  const parseLocationSelection = (value) => {
    if (!value || value === 'all') return { branchIds: undefined, countryIds: undefined };
    const raw = String(value);
    const [kind, idStr] = raw.split(':');
    const id = Number(idStr);
    if (!Number.isFinite(id)) return { branchIds: undefined, countryIds: undefined };
    if (kind === 'branch') return { branchIds: [id], countryIds: undefined };
    if (kind === 'country') return { branchIds: undefined, countryIds: [id] };
    return { branchIds: undefined, countryIds: undefined };
  };

  const parseStatusFilter = (value) => {
    if (!value || value === 'all') return { kind: 'all', value: null };
    const raw = String(value);
    if (raw === 'correction_pending') return { kind: 'correction_pending', value: null };
    if (raw.startsWith('status:')) return { kind: 'status', value: raw.slice('status:'.length) };
    return { kind: 'all', value: null };
  };

  const combineDateAndTime = (dateInput, timeHHmm) => {
    if (!dateInput || !timeHHmm) return null;
    const base = dateInput instanceof Date ? new Date(dateInput) : new Date(dateInput);
    if (Number.isNaN(base.getTime())) return null;
    const [hh, mm] = String(timeHHmm).split(':').map((x) => Number(x));
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
    base.setHours(hh, mm, 0, 0);
    return base;
  };

  useEffect(() => {
    let cancelled = false;

    const selectedEmployeeIds = (selectedEmployees || [])
      .map((u) => u?.id)
      .filter((id) => id !== null && id !== undefined);

    const loadFilterOptions = async () => {
      try {
        if (!companyId || !userId) return;

        const [depsRes, branchesRes, countriesRes] = await Promise.all([
          getDepartments(companyId).catch(() => null),
          getBranches(companyId).catch(() => null),
          getUsersCountries(userId).catch(() => null),
        ]);

        if (cancelled) return;

        const deps = (depsRes?.payload || [])
          .filter((d) => d && d.id != null)
          .map((d) => ({ id: String(d.id), name: d.name }));
        setDepartmentOptions([{ id: 'all', name: t('All Departments') }, ...deps]);

        const countries = (countriesRes?.payload || [])
          .filter((c) => c && c.id != null)
          .map((c) => ({ id: `country:${c.id}`, name: `${t('Country')}: ${c.name}` }));
        const branches = (branchesRes?.payload || [])
          .filter((b) => b && b.id != null)
          .map((b) => ({ id: `branch:${b.id}`, name: `${t('Branch')}: ${b.name}` }));

        setLocationOptions([{ id: 'all', name: t('All Locations') }, ...countries, ...branches]);
      } catch (e) {
        // Non-blocking: keep defaults
        console.warn('Failed to load Team Attendance filter options', e);
      }
    };

    loadFilterOptions();
    return () => {
      cancelled = true;
    };
  }, [companyId, userId, t]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const selectedEmployeeIds = (selectedEmployees || [])
          .map((u) => u?.id)
          .filter((id) => id !== null && id !== undefined);

        const from = selectedDateRange?.start ? new Date(selectedDateRange.start) : null;
        const to = selectedDateRange?.end ? new Date(selectedDateRange.end) : null;
        const fromSafe = from && !Number.isNaN(from.getTime()) ? new Date(from) : null;
        const toSafe = to && !Number.isNaN(to.getTime()) ? new Date(to) : null;
        if (fromSafe) fromSafe.setHours(0, 0, 0, 0);
        if (toSafe) toSafe.setHours(23, 59, 59, 999);

        if (!userId) {
          setAttendanceData([]);
          setLoadError(t('Missing user context.'));
          return;
        }

        const departmentIds = selectedDepartment !== 'all'
          ? [Number(selectedDepartment)].filter((n) => Number.isFinite(n))
          : undefined;
        const { branchIds, countryIds } = parseLocationSelection(selectedLocation);

        // Map status filter to server enum names
        const statusFilter = parseStatusFilter(selectedStatusFilter);
        const isCorrectionPending = statusFilter.kind === 'correction_pending';
        let statuses = undefined;
        if (statusFilter.kind === 'status' && statusFilter.value) {
          statuses = STATUS_LABEL_TO_ENUM[statusFilter.value] || undefined;
        }

        // When correction_pending is active, fetch all (no server pagination)
        const effectivePageSize = isCorrectionPending ? 0 : pageSize;
        const effectivePageNumber = isCorrectionPending ? 1 : pageNumber;

        const baseParams = {
          userId,
          from: fromSafe || selectedDateRange?.start,
          to: toSafe || selectedDateRange?.end,
          departmentIds,
          countryIds,
          branchIds,
          searchMethod: 'AND',
          sortBy: sortConfig?.key || 'date',
          sortDirection: sortConfig?.direction || 'desc',
          pageNumber: effectivePageNumber,
          pageSize: effectivePageSize,
          statuses,
          includeEmptyNonWorkingDays: showNonWorkingDays,
        };

        // Fetch users and corrections in parallel first
        const [usersRes, correctionsRes] = await Promise.all([
          companyId ? FetchUsers('Company', companyId, false).catch(() => null) : Promise.resolve(null),
          getAssignedCorrectionRequests().catch(() => null),
        ]);

        if (cancelled) return;

        const usersPayload = usersRes?.payload || [];
        setCompanyUsers(usersPayload);

        // Resolve the final userIds for the single attendance call
        let resolvedUserIds = selectedEmployeeIds.length ? selectedEmployeeIds : undefined;
        if (!resolvedUserIds) {
          const companyUserIds = usersPayload.map((u) => u?.id).filter(Boolean);
          if (companyUserIds.length > 0) resolvedUserIds = companyUserIds;
        }

        // The UsersStatuses report drops any shift without a clock-out ("ended shifts only"), which is
        // why an unclosed entry used to render "-" for BOTH punches — indistinguishable from a day
        // nothing was entered on. Fetch those rows separately and overlay them below. HR/Admin only, and
        // best-effort: a failure here must never take the grid down, it just costs the extra signal.
        const [statusesRes, openShiftsRes] = await Promise.all([
          getUsersStatuses({
            ...baseParams,
            userIds: resolvedUserIds,
          }),
          canEditAttendance && fromSafe && toSafe
            ? getOpenShifts(
              // Widened one day on each side: the grid's bounds are employee-LOCAL dates while the
              // server filters on ClockInTimeUtc, so a boundary day could otherwise fall outside the
              // UTC window by up to the timezone offset. Rows are matched back to their day
              // client-side, so the extra day costs nothing.
              ymdLocal(new Date(fromSafe.getTime() - 86400000)),
              ymdLocal(new Date(toSafe.getTime() + 86400000)),
              // Only an EXPLICIT employee selection is sent. `resolvedUserIds` falls back to every user
              // in the company, which on a large tenant would blow past the URL length limit; the
              // endpoint already scopes to the caller's company on its own, so sending nothing is both
              // shorter and equivalent.
              selectedEmployeeIds.length ? selectedEmployeeIds : undefined
            ).catch(() => null)
            : Promise.resolve(null),
        ]);

        if (cancelled) return;

        if (statusesRes?.errors?.length) {
          setLoadError(statusesRes.errors[0]?.description || t('Failed to load team attendance data.'));
          setAttendanceData([]);
          return;
        }

        const paginatedPayload = statusesRes?.payload;
        const statusesPayload = paginatedPayload?.items || [];

        // Store pagination metadata and KPIs
        setTotalCount(paginatedPayload?.totalCount || 0);
        setTotalPages(paginatedPayload?.totalPages || 0);
        setServerKpis({
          attendanceRate: paginatedPayload?.attendanceRate || 0,
          partialCount: paginatedPayload?.partialCount || 0,
          totalOvertime: paginatedPayload?.totalOvertime || 0,
        });

        const correctionRequests = correctionsRes?.payload || [];
        const correctionByUserDay = new Map();
        for (const r of correctionRequests) {
          const isPending = String(r?.status || '').toLowerCase() === 'pending';
          if (!isPending) continue;
          const key = `${r?.userId || ''}_${ymdLocal(r?.date)}`;
          if (!key || key.endsWith('_')) continue;
          if (!correctionByUserDay.has(key)) {
            correctionByUserDay.set(key, r);
          }
        }

        // Open (clock-out-less) shifts, keyed the same way the rows are, so a day can be told apart
        // from "nothing recorded". At most one open shift per employee is possible (the server refuses
        // a second one), so a last-writer-wins map is exact rather than lossy.
        const openShiftByUserDay = new Map();
        for (const os of (openShiftsRes?.payload || [])) {
          const key = `${os?.userId || ''}_${ymdLocal(os?.clockInTime)}`;
          if (!key || key.endsWith('_')) continue;
          openShiftByUserDay.set(key, os);
        }

        const rows = statusesPayload.map((s) => {
          const u = s?.user;
          const dateKey = ymdLocal(s?.date);
          const correction = correctionByUserDay.get(`${u?.id || ''}_${dateKey}`);
          const openShift = openShiftByUserDay.get(`${u?.id || ''}_${dateKey}`) || null;

          const clockIns = s?.clockInTimes || [];
          const clockOuts = s?.clockOutTimes || [];
          // The report never carries the open row, so its clock-in has to come from the overlay.
          // Without this the column reads "-" and HR has no idea a half-entered backfill exists.
          const firstIn = clockIns?.[0] ?? (openShift ? openShift.clockInTime : undefined);
          const lastOut = clockOuts?.[clockOuts.length - 1];

          const requestId = correction?.requestId;
          const correctionPending = Boolean(requestId);

          return {
            id: `${u?.id || 'user'}_${dateKey || Math.random()}`,
            requestId: requestId ?? null,
            userId: u?.id,
            dateKey,
            employee: u?.fullName || u?.userName || u?.email || u?.id,
            employeeId: u?.employeeId || u?.id,
            employeeTitle: u?.title || '-',
            department: u?.department?.name || '-',
            departmentId: u?.departmentId ?? null,
            branchId: u?.branchId ?? u?.branch?.id ?? null,
            countryId: u?.countryId ?? u?.country?.id ?? null,
            date: s?.date,
            clockIn: formatTimeHHmm(firstIn),
            clockOut: formatTimeHHmm(lastOut),
            totalHours: Number(s?.totalWorkHours || 0),
            overtimeHours: Number(s?.overtimeHours || 0),
            status: mapUserStatusToLabel(s?.status),
            remarks: correction?.reason || s?.remarks || '',
            openShift,
            correctionPending,
            correctionRequest: correctionPending ? {
              requestId: correction?.requestId,
              date: correction?.date,
              correctionType: formatCorrectionType(correction?.type),
              detailedReason: correction?.reason,
              supportingDocuments: correction?.attachments || [],
              submittedAt: correction?.submittedDate,
              requestedClockIn: correction?.requestedCheckIn ?? null,
              requestedClockOut: correction?.requestedCheckOut ?? null,
            } : null,
          };
        });

        setAttendanceData(rows);
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load team attendance from UsersStatuses', e);
        setLoadError(t('Failed to load team attendance data.'));
        setAttendanceData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [userId, companyId, selectedDateRange, selectedDepartment, selectedLocation,
      selectedEmployees, selectedStatusFilter, sortConfig, pageNumber, pageSize,
      showNonWorkingDays, reloadCounter, t]);

  // With server-side filtering, employee dropdown shows all employees
  // (the server handles status filtering, so no client-side cascading needed)
  const statusAllowedUserIds = null;

  const employeesForOptions = (companyUsers || [])
    .filter((u) => matchesLocationForUser(u, selectedLocation))
    .filter((u) => matchesDepartmentForUser(u, selectedDepartment));

  const employeesForOptionsWithStatus = statusAllowedUserIds
    ? employeesForOptions.filter((u) => statusAllowedUserIds.has(u?.id))
    : employeesForOptions;

  const sortedEmployeesForOptionsWithStatus = useMemo(() => {
    const getEmployeeLabel = (u) => u?.fullName || u?.userName || u?.email || String(u?.id || '');
    return [...(employeesForOptionsWithStatus || [])].sort((a, b) =>
      getEmployeeLabel(a).localeCompare(getEmployeeLabel(b), undefined, { sensitivity: 'base', numeric: true })
    );
  }, [employeesForOptionsWithStatus]);

  const selectedEmployeeIdsSet = new Set(
    (selectedEmployees || []).map((u) => u?.id).filter((id) => id !== null && id !== undefined)
  );

  const usersForDepartmentOptions = (companyUsers || [])
    .filter((u) => matchesLocationForUser(u, selectedLocation))
    .filter((u) => (selectedEmployeeIdsSet.size ? selectedEmployeeIdsSet.has(u?.id) : true));

  const usersForDepartmentOptionsWithStatus = statusAllowedUserIds
    ? usersForDepartmentOptions.filter((u) => statusAllowedUserIds.has(u?.id))
    : usersForDepartmentOptions;

  const allowedDepartmentIds = new Set(
    usersForDepartmentOptionsWithStatus
      .map((u) => u?.departmentId ?? u?.department?.id)
      .filter((x) => x !== null && x !== undefined)
      .map((x) => String(x))
  );

  const derivedDepartmentOptions = [{ id: 'all', name: t('All Departments') }, ...(departmentOptions || [])
    .filter((d) => d && d.id != null)
    .filter((d) => String(d.id) !== 'all')
    .filter((d) => allowedDepartmentIds.size ? allowedDepartmentIds.has(String(d.id)) : true)
  ];

  const usersForLocationOptions = (companyUsers || [])
    .filter((u) => matchesDepartmentForUser(u, selectedDepartment))
    .filter((u) => (selectedEmployeeIdsSet.size ? selectedEmployeeIdsSet.has(u?.id) : true));

  const usersForLocationOptionsWithStatus = statusAllowedUserIds
    ? usersForLocationOptions.filter((u) => statusAllowedUserIds.has(u?.id))
    : usersForLocationOptions;

  const allowedBranchIds = new Set(
    usersForLocationOptionsWithStatus
      .map((u) => u?.branchId ?? u?.branch?.id)
      .filter((x) => x !== null && x !== undefined)
      .map((x) => Number(x))
      .filter((x) => Number.isFinite(x))
  );
  const allowedCountryIds = new Set(
    usersForLocationOptionsWithStatus
      .map((u) => u?.countryId ?? u?.country?.id)
      .filter((x) => x !== null && x !== undefined)
      .map((x) => Number(x))
      .filter((x) => Number.isFinite(x))
  );

  const derivedLocationOptions = [{ id: 'all', name: t('All Locations') }, ...(locationOptions || [])
    .filter((l) => l && l.id != null)
    .filter((l) => String(l.id) !== 'all')
    .filter((l) => {
      const raw = String(l.id);
      const [kind, idStr] = raw.split(':');
      const id = Number(idStr);
      if (!Number.isFinite(id)) return true;
      if (kind === 'branch') return allowedBranchIds.size ? allowedBranchIds.has(id) : true;
      if (kind === 'country') return allowedCountryIds.size ? allowedCountryIds.has(id) : true;
      return true;
    })
  ];

  const statusValues = ['Present', 'Partial', 'Absent', 'Leave', 'Holiday', 'Non-Working Day', 'Future'];

  const hasPendingCorrections = true;

  // Keep selections valid when options shrink
  useEffect(() => {
    const allowedEmployeeIds = new Set((employeesForOptionsWithStatus || []).map((u) => u?.id).filter(Boolean));
    const pruned = (selectedEmployees || []).filter((u) => allowedEmployeeIds.has(u?.id));
    if (pruned.length !== (selectedEmployees || []).length) {
      setSelectedEmployees(pruned);
    }
  }, [selectedLocation, selectedDepartment, companyUsers]);

  useEffect(() => {
    if (selectedDepartment === 'all') return;
    const exists = derivedDepartmentOptions?.some((d) => String(d?.id) === String(selectedDepartment));
    if (!exists) setSelectedDepartment('all');
  }, [selectedDepartment, derivedDepartmentOptions]);

  useEffect(() => {
    if (selectedLocation === 'all') return;
    const exists = derivedLocationOptions?.some((l) => String(l?.id) === String(selectedLocation));
    if (!exists) setSelectedLocation('all');
  }, [selectedLocation, derivedLocationOptions]);

  const handleFilterChange = (filterType, value) => {
    setPageNumber(1);
    switch (filterType) {
      case 'dateRange':
        setSelectedDateRange(value);
        break;
      case 'department':
        setSelectedDepartment(value);
        break;
      case 'location':
        setSelectedLocation(value);
        break;
      case 'status':
        setSelectedStatusFilter(value);
        break;
      case 'employees':
        setSelectedEmployees(value || []);
        break;
      default:
        break;
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setPageNumber(1);
    setSortConfig({ key, direction });
  };

  const handleAddMissingLog = (record) => {
    if (!canEditAttendance) {
      showToast(t('Only HR can add missing attendance logs.'), 'info');
      return;
    }
    setSelectedRecord(record);
    setShowAddLogModal(true);
  };

  const handleAddRemark = (record) => {
    if (!canEditAttendance) {
      showToast(t('Only HR can add remarks.'), 'info');
      return;
    }
    setSelectedRecord(record);
    setShowRemarkModal(true);
  };

  const handleApproveReject = (record) => {
    setSelectedRecord(record);
    setShowApproveRejectModal(true);
  };

  const handleSubmitCorrectionDecision = async ({ isApproved, comment } = {}) => {
    if (!selectedRecord) return;

    const requestId = selectedRecord?.requestId ?? selectedRecord?.correctionRequest?.requestId;
    if (!requestId) return;

    try {
      const res = await decideCorrectionRequest({
        requestId,
        isApproved: !!isApproved,
        comment: typeof comment === 'string' && comment.trim().length ? comment.trim() : undefined,
      });

      if (res?.status !== 200 || res?.payload !== true) {
        const message = extractApiErrorMessage(
          { response: { data: res, status: res?.status } },
          t('Failed to submit decision. Please try again.')
        );
        showToast(message, 'error');
        return;
      }

      setAttendanceData((prev) => prev?.map((record) =>
        record?.id === selectedRecord?.id
          ? {
            ...record,
            correctionPending: false,
            remarks: !isApproved
              ? `${record?.remarks || ''}${(typeof comment === 'string' && comment.trim().length) ? ` (Rejected: ${comment.trim()})` : ' (Rejected)'}`
              : `${record?.remarks || ''} (Approved)`,
          }
          : record
      ));

      setShowApproveRejectModal(false);
      setSelectedRecord(null);
      setReloadCounter((x) => x + 1);
    } catch (e) {
      console.error('Failed to submit correction decision', e);
      showToast(
        extractApiErrorMessage(e, t('Failed to submit decision. Please try again.')),
        'error'
      );
    }
  };


  const extractApiErrorMessage = (e, fallback) => {
    if (!e) return fallback;
    if (typeof e === 'string') return e;

    const data = e?.response?.data || e?.data || e;
    const errors = data?.errors;
    if (Array.isArray(errors) && errors.length > 0) {
      const descriptions = errors
        .map((x) => x?.description || x?.message || x?.code)
        .filter(Boolean)
        .map((x) => String(x));

      const first = errors?.[0];
      const code = first?.code;
      const description = first?.description || first?.message;

      // An unclosed entry blocking the write is a different problem from a genuine overlap, and it has
      // a different remedy: finish that entry, don't move this one. The server gives it its own code so
      // the message can say so instead of the useless "overlaps another shift".
      if (code === 'OpenShiftBlocking') {
        const openShift = first?.details?.openShift;
        const when = openShift?.start ? new Date(openShift.start) : null;
        const whenText = when && !Number.isNaN(when.getTime())
          ? appDateTime(when)
          : null;
        return [
          t('This employee has an entry with no clock-out yet, so no other entry can be added until it is completed.'),
          whenText ? `${t('Unclosed entry started')}: ${whenText}` : null,
          t('Open that day and use "Add the missing clock-out" to finish it.'),
        ].filter(Boolean).join('\n');
      }

      if (code === 'NoOpenShiftToClose') {
        return t('There is no unclosed entry at or before that time. Use "Add Attendance Entry" to record a complete shift instead.');
      }

      if (code === 'ShiftOverlap') {
        const conflict = first?.details?.conflict;
        const attempted = first?.details?.attempted;

        const fmt = (d) => {
          const dt = d instanceof Date ? d : new Date(d);
          if (Number.isNaN(dt.getTime())) return String(d || '');
          return appDateTime(dt);
        };

        if (conflict?.id) {
          const attemptedText = attempted?.start && attempted?.end
            ? `${fmt(attempted.start)} → ${fmt(attempted.end)}`
            : null;
          const conflictText = `${fmt(conflict.start)} → ${conflict.end ? fmt(conflict.end) : t('Open shift')}`;
          return [
            t('This shift overlaps an existing shift.'),
            attemptedText ? `${t('Attempted')}: ${attemptedText}` : null,
            `${t('Conflicts with shift')} #${conflict.id}: ${conflictText}`,
          ].filter(Boolean).join('\n');
        }
      }

      const fromErrors = descriptions.length ? descriptions.join('\n') : description;
      if (fromErrors) return fromErrors;
    }

    const msg = data?.message || e?.message;
    if (msg) return String(msg);

    const status = e?.status || e?.response?.status;
    const statusText = e?.statusText || e?.response?.statusText;
    if (status) return statusText ? `${status} ${statusText}` : String(status);

    return fallback;
  };

  const handleSaveRemark = async (remark) => {
    if (!selectedRecord?.userId || !selectedRecord?.date) return;
    if (!canEditAttendance) return;

    try {
      // Use the same row dateKey to avoid timezone drift, and send a JSON date-time value that the server can bind.
      const dateKey = selectedRecord?.dateKey || ymdLocal(selectedRecord?.date);
      const dateValue = `${dateKey}T00:00:00`;

      const res = await updateAttendanceRemark({
        userId: selectedRecord.userId,
        date: dateValue,
        remark,
      });

      if (res?.status !== 200) {
        throw new Error('Remark API failed');
      }

      setAttendanceData((prev) => prev?.map((r) =>
        r?.id === selectedRecord?.id ? { ...r, remarks: remark } : r
      ));
      setShowRemarkModal(false);
      setSelectedRecord(null);
      setReloadCounter((x) => x + 1);
    } catch (e) {
      console.error('Failed to save remark', e);
      showToast(extractApiErrorMessage(e) || t('Failed to save remark. Please try again.'), 'error');
    }
  };

  const handleSaveMissingLog = async ({ inTime, outTime, comments }) => {
    if (!selectedRecord?.userId || !selectedRecord?.date) return;
    if (!canEditAttendance) return;
    if (savingMissingLog) return;

    const dayKey = selectedRecord?.dateKey || ymdLocal(selectedRecord?.date);
    const day = new Date(`${dayKey}T00:00:00`);
    const clockIn = inTime ? combineDateAndTime(day, inTime) : null;
    const clockOut = outTime ? combineDateAndTime(day, outTime) : null;
    const openShift = selectedRecord?.openShift || null;

    // 🔴 DUPLICATE-ENTRY GUARD. When the day already carries an unclosed entry, the ONLY thing this
    // dialog may do is close it. It must never take the addShift path (that would create a second,
    // overlapping row) and never take the clock-in path (that would create a second OPEN row, which
    // is exactly what the blank version of this dialog used to do). The server enforces the same rule
    // — this is the half that keeps HR from ever reaching the error.
    if (openShift) {
      if (!clockOut) {
        showToast(t('Enter the clock-out time to close this entry.'), 'info');
        return;
      }
      try {
        setSavingMissingLog(true);
        const hoursOffset = clockOut.getTimezoneOffset() / -60;
        const res = await clock(ClockingType.ClockOut, {
          time: clockOut,
          hoursOffset,
          userId: selectedRecord.userId,
          clientType: ClientType.Web,
          comments: comments || undefined,
          isManualEntry: true,
        });
        if (res?.errors?.length) {
          showToast(extractApiErrorMessage({ data: res }, t('Failed to close the entry. Please try again.')), 'error');
          return;
        }
        setShowAddLogModal(false);
        setSelectedRecord(null);
        setReloadCounter((x) => x + 1);
      } catch (e) {
        console.error('Failed to close open shift', e);
        showToast(extractApiErrorMessage(e, t('Failed to close the entry. Please try again.')), 'error');
      } finally {
        setSavingMissingLog(false);
      }
      return;
    }

    try {
      setSavingMissingLog(true);
      let res = null;
      if (clockIn && clockOut) {
        const hoursOffset = clockIn.getTimezoneOffset() / -60;
        res = await addShift(
          {
            clockInTime: clockIn,
            clockOutTime: clockOut,
            userId: selectedRecord.userId,
            clockInTimeUtc: clockIn.toUTCString(),
            clockInClientType: ClientType.Web,
            clockInImageUrl: "",
            clockInAddress: "",
            clockInCountry: "",
            clockOutTimeUtc: clockOut.toUTCString(),
            clockOutClientType: ClientType.Web,
            clockOutImageUrl: "",
            clockOutAddress: "",
            clockOutCountry: "",
            clockInComments: comments || "",
            clockOutComments: comments || "",
          },
          hoursOffset
        );
      } else if (clockIn) {
        const hoursOffset = clockIn.getTimezoneOffset() / -60;
        res = await clock(ClockingType.ClockIn, {
          time: clockIn,
          hoursOffset,
          userId: selectedRecord.userId,
          clientType: ClientType.Web,
          comments: comments || undefined,
          isManualEntry: true,
        });
      } else if (clockOut) {
        const hoursOffset = clockOut.getTimezoneOffset() / -60;
        res = await clock(ClockingType.ClockOut, {
          time: clockOut,
          hoursOffset,
          userId: selectedRecord.userId,
          clientType: ClientType.Web,
          comments: comments || undefined,
          isManualEntry: true,
        });
      } else {
        showToast(t('Please provide a clock-in or clock-out time.'), 'info');
        return;
      }

      // The result was previously discarded, so a refusal that came back as a Result body rather than
      // an HTTP error closed the dialog looking like a success and HR was told nothing at all.
      if (res?.errors?.length) {
        showToast(extractApiErrorMessage({ data: res }, t('Failed to add missing log. Please try again.')), 'error');
        return;
      }

      setShowAddLogModal(false);
      setSelectedRecord(null);
      setReloadCounter((x) => x + 1);
    } catch (e) {
      console.error('Failed to add missing log', e);
      showToast(extractApiErrorMessage(e) || t('Failed to add missing log. Please try again.'), 'error');
    } finally {
      setSavingMissingLog(false);
    }
  };

  const handleExportReport = async (format) => {
    if (exporting) return;

    const from = new Date(selectedDateRange?.start);
    const to = new Date(selectedDateRange?.end);
    from.setHours(0, 0, 0, 0);
    to.setHours(23, 59, 59, 999);

    const selectedEmployeeIds = (selectedEmployees || [])
      .map((u) => u?.id)
      .filter((id) => id !== null && id !== undefined);

    const departmentIds = selectedDepartment !== 'all' ? [Number(selectedDepartment)].filter(Number.isFinite) : undefined;
    const { branchIds, countryIds } = parseLocationSelection(selectedLocation);

    // Match the Reports UX behavior: normalize dates for server parsing.
    const fromNormalized = new Date(from.getTime() - from.getTimezoneOffset() * 60 * 1000);
    const toNormalized = new Date(to.getTime() - to.getTimezoneOffset() * 60 * 1000);

    const normalizedFormat = format === 'excel' ? 'xlsx' : format;

    try {
      setExporting(true);
      // Map current status filter to server enum names for export
      const statusFilter = parseStatusFilter(selectedStatusFilter);
      let exportStatuses = undefined;
      if (statusFilter.kind === 'status' && statusFilter.value) {
        exportStatuses = STATUS_LABEL_TO_ENUM[statusFilter.value] || undefined;
      }

      await exportUsersStatuses(
        {
          userId,
          from: fromNormalized,
          to: toNormalized,
          userIds: selectedEmployeeIds?.length ? selectedEmployeeIds : undefined,
          departmentIds,
          branchIds,
          countryIds,
          searchMethod: 'AND',
          sortBy: sortConfig?.key || 'date',
          sortDirection: sortConfig?.direction || 'desc',
          statuses: exportStatuses,
          includeEmptyNonWorkingDays: showNonWorkingDays,
        },
        normalizedFormat
      );
    } catch (e) {
      console.error('Export failed', e);
      showToast(extractApiErrorMessage(e) || t('Export failed. Please try again.'), 'error');
    } finally {
      setExporting(false);
    }
  };

  // Only client-side filter remaining: correction_pending
  const filteredAttendanceData = useMemo(() => {
    const statusFilter = parseStatusFilter(selectedStatusFilter);
    if (statusFilter.kind === 'correction_pending') {
      return (attendanceData || []).filter((r) => r?.correctionPending === true);
    }
    return attendanceData || [];
  }, [attendanceData, selectedStatusFilter]);

  const kpis = serverKpis;

  const handleAddAttendanceEntry = () => {
    if (!canEditAttendance) {
      showToast(t('Only HR can add attendance entries.'), 'info');
      return;
    }
    setSelectedRecord(null);
    setShowAddAttendanceModal(true);
  };

  const handleViewDetails = (record) => {
    if (!record?.userId || !record?.dateKey) return;
    setSelectedRecord(record);
    setShowDayDetailsModal(true);
  };

  const handleSaveAttendanceEntry = async ({ employeeUserId, date, inTime, outTime, remarks }) => {
    if (!canEditAttendance) return;
    const clockIn = combineDateAndTime(date, inTime);
    const clockOut = combineDateAndTime(date, outTime);
    if (!employeeUserId || !clockIn || !clockOut) {
      showToast(t('Please provide employee, date, clock-in, and clock-out.'), 'info');
      return;
    }

    try {
      const hoursOffset = clockIn.getTimezoneOffset() / -60;
      const res = await addShift(
        {
          clockInTime: clockIn,
          clockOutTime: clockOut,
          userId: employeeUserId,
          clockInTimeUtc: clockIn.toUTCString(),
          clockInClientType: ClientType.Web,
          clockInImageUrl: "",
          clockInAddress: "",
          clockInCountry: "",
          clockOutTimeUtc: clockOut.toUTCString(),
          clockOutClientType: ClientType.Web,
          clockOutImageUrl: "",
          clockOutAddress: "",
          clockOutCountry: "",
          clockInComments: remarks || "",
          clockOutComments: remarks || "",
        },
        hoursOffset
      );

      // Previously discarded, so a refusal returned in the Result body closed the dialog silently and
      // HR believed the entry had been saved. An "OpenShiftBlocking" refusal in particular is the one
      // HR most needs to read, because it names the unclosed entry they have to finish first.
      if (res?.errors?.length) {
        showToast(extractApiErrorMessage({ data: res }, t('Failed to add attendance entry. Please try again.')), 'error');
        return;
      }

      setShowAddAttendanceModal(false);
      setSelectedRecord(null);
      setReloadCounter((x) => x + 1);
    } catch (e) {
      console.error('Failed to add attendance entry', e);
      showToast(extractApiErrorMessage(e) || t('Failed to add attendance entry. Please try again.'), 'error');
    }
  };

  return (
    <PageShell
      title={t('Team Attendance')}
      description={t("Monitor and manage your team's attendance health")}
      icon={Users}
      accentColor="blue"
      maxWidth="1600px"
    >
      <div className="space-y-6">
        {loadError ? (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
            {loadError}
          </div>
        ) : null}

        {loading ? (
          <LoadingShimmer />
        ) : (
          <>
            <KPIDashboard
              attendanceRate={Number(kpis?.attendanceRate || 0)}
              partialCount={Number(kpis?.partialCount || 0)}
              totalOvertime={Number(kpis?.totalOvertime || 0)}
            />

            <TeamFilters
              selectedDateRange={selectedDateRange}
              selectedDepartment={selectedDepartment}
              selectedLocation={selectedLocation}
              selectedStatusFilter={selectedStatusFilter}
              statusOptions={statusValues}
              hasPendingCorrections={hasPendingCorrections}
              employees={sortedEmployeesForOptionsWithStatus}
              selectedEmployees={selectedEmployees}
              departments={derivedDepartmentOptions}
              locations={derivedLocationOptions}
              onFilterChange={handleFilterChange}
              onExport={handleExportReport}
              exportDisabled={exporting}
              showNonWorkingDays={showNonWorkingDays}
              onShowNonWorkingDaysChange={(val) => {
                setShowNonWorkingDays(val);
                setPageNumber(1);
              }}
            />

            <AttendanceTable
              attendanceData={filteredAttendanceData}
              totalRecordCount={totalCount}
              sortConfig={sortConfig}
              onSort={handleSort}
              onViewDetails={handleViewDetails}
              onAddMissingLog={handleAddMissingLog}
              onAddRemark={handleAddRemark}
              onApproveReject={handleApproveReject}
              onAddAttendanceEntry={handleAddAttendanceEntry}
              canAddAttendanceEntry={canEditAttendance}
              canAddMissingLog={canEditAttendance}
            />

            {/* Pagination bar */}
            {parseStatusFilter(selectedStatusFilter).kind !== 'correction_pending' && totalCount > 0 && (
              <div className="flex items-center justify-between mt-4 flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <label htmlFor="pageSize" className="text-sm text-muted-foreground whitespace-nowrap">
                    {t('Rows per page')}
                  </label>
                  <select
                    id="pageSize"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPageNumber(1);
                    }}
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {[10, 25, 50, 100].map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                  <span className="text-sm text-muted-foreground">
                    {t('totalRecords', { count: totalCount })}
                  </span>
                </div>
                <Pagination
                  currentPage={pageNumber}
                  totalPages={totalPages}
                  onPageChange={setPageNumber}
                />
              </div>
            )}
          </>
        )}
      </div>

      <AddMissingLogModal
        open={showAddLogModal}
        record={selectedRecord}
        onClose={() => {
          setShowAddLogModal(false);
          setSelectedRecord(null);
        }}
        onSave={handleSaveMissingLog}
        saving={savingMissingLog}
      />

      <AddRemarkModal
        open={showRemarkModal}
        record={selectedRecord}
        onClose={() => {
          setShowRemarkModal(false);
          setSelectedRecord(null);
        }}
        onSave={handleSaveRemark}
      />

      <CorrectionRequestReviewModal
        open={showApproveRejectModal}
        record={selectedRecord}
        onClose={() => {
          setShowApproveRejectModal(false);
          setSelectedRecord(null);
        }}
        onApprove={() => handleSubmitCorrectionDecision({ isApproved: true })}
        onReject={(reason) => handleSubmitCorrectionDecision({ isApproved: false, comment: reason })}
      />

      <AddAttendanceEntryModal
        open={showAddAttendanceModal}
        users={companyUsers}
        presetUserId={selectedRecord?.userId}
        presetDate={selectedRecord?.date}
        onClose={() => {
          setShowAddAttendanceModal(false);
          setSelectedRecord(null);
        }}
        onSave={handleSaveAttendanceEntry}
      />

      <DayRecordDetailsModal
        open={showDayDetailsModal}
        record={selectedRecord}
        canEdit={canEditAttendance}
        onClose={() => {
          setShowDayDetailsModal(false);
          setSelectedRecord(null);
        }}
        onUpdated={() => setReloadCounter((x) => x + 1)}
      />

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </PageShell>
  );
};

export default TeamAttendancePage;